import { useLingui } from '@lingui/react/macro';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Call, Sms } from 'iconsax-reactjs';
import type { FC } from 'react';
import { useMe } from '@/account/profile/api';
import { useHasAccessToken } from '@/auth/api';
import { GoogleLoginButton } from '@/auth/GoogleLoginButton';
import { GOOGLE_CLIENT_ID } from '@/base/constants';
import { EmptyLayout } from '@/base/layouts/EmptyLayout';
import { PageMeta } from '@/base/PageMeta';
import { Redirect, useLocation } from '@/base/router';
import { LanguageSelect } from '@/settings/LanguageSelect';
import { ThemeSelect } from '@/settings/ThemeSelect';
import { Button } from '@/shared/Button';
import { OnboardingSlider } from './OnboardingSlider';

export const OnboardingPage: FC = () => {
  const { t } = useLingui();
  const [, navigate] = useLocation();
  const hasAccessToken = useHasAccessToken();
  const me = useMe();

  const nextUrl = () =>
    !me.data?.phone_number || !me.data.kyc_info_status
      ? '/account/identification?open'
      : '/dashboard';

  if (hasAccessToken && me.data) {
    return <Redirect workspace="app" to={nextUrl()} />;
  }

  const handlePhoneClick = () => navigate('app', '/auth/phone');

  const handleEmailClick = () => navigate('app', '/auth/email');

  const handleGoogleFinish = () => navigate('app', '/dashboard');

  return (
    <>
      <PageMeta title={t`Get Started`} />
      <EmptyLayout>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <div className="min-h-svh bg-white dark:bg-neutral-950 flex flex-col items-center justify-between py-8 max-w-md mx-auto desktop:max-w-xl relative">
            <div className="flex gap-2 items-center absolute top-3 end-3 z-10">
              <LanguageSelect
                mini
                size={8}
                arrow={false}
                className="w-auto px-2"
              />
              <ThemeSelect size={8} arrow={false} className="px-0 !w-8" mini />
            </div>

            <div className="flex-1 flex flex-col items-center justify-start w-full">
              <OnboardingSlider />
            </div>

            <div className="w-full flex flex-col gap-3 mt-auto pt-8 px-4">
              <Button
                variant="brand"
                size={12}
                block
                onClick={handlePhoneClick}
                className="w-full flex items-center justify-center gap-2"
              >
                <Call size={20} variant="Bold" />
                {t`Continue with Phone Number`}
              </Button>

              <Button
                variant="primary"
                size={12}
                block
                onClick={handleEmailClick}
                className="w-full flex items-center justify-center gap-2"
              >
                <Sms size={20} variant="Bold" />
                {t`Continue with Email`}
              </Button>

              <GoogleLoginButton
                className="w-full"
                onFinish={handleGoogleFinish}
              />
            </div>
          </div>
        </GoogleOAuthProvider>
      </EmptyLayout>
    </>
  );
};
